/* eslint-disable max-len */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import RouteSeo from '../Seo/RouteSeo';
import routes from '../../routes';

const NavigationLayout = lazy(() => import('../../containers/NavigationLayout'));
const SignedOutLayout = lazy(() => import('../../containers/SignedOutLayout'));
const SmartRedirectLayout = lazy(() => import('../../containers/SmartRedirectLayout/SmartRedirectLayout'));
const WebViewLayout = lazy(() => import('../../containers/WebViewLayout/WebViewLayout'));

const AllPublishers = lazy(() => import('../../containers/AllPublishers'));
const AmbassadorLp = lazy(() => import('../../containers/AmbassadorLP/AmbassadorLP'));
const AmbassadorLPWebView = lazy(() => import('../../containers/AmbassadorLP/AmbassadorLPWebView'));
const AmbassadorLPWebViewToken = lazy(() => import('../../containers/AmbassadorLP/AmbassadorLPWebViewToken'));
const AppRedirect = lazy(() => import('../../containers/AppRedirect/AppRedirect'));
const Article = lazy(() => import('../../containers/Article'));
const SharedArticle = lazy(() => import('../../containers/Article').then((module) => ({ default: module.SharedArticle })));
const Bookmarks = lazy(() => import('../../containers/Bookmarks'));
const BracketChallenge = lazy(() => import('../../containers/BracketChallenge/BracketChallenge'));
const BracketChallengeLp = lazy(() => import('../../containers/BracketChallengeLp/BracketChallengeLp'));
const BulkSubscriptionLp = lazy(() => import('../../containers/BulkSubscriptionLp/BulkSubscriptionLp'));
const Channel = lazy(() => import('../../containers/Channel'));
const ChannelAbout = lazy(() => import('../../containers/Channel/ChannelAbout'));
const ChannelCategories = lazy(() => import('../../containers/Channel/ChannelCategories'));
const ChannelRecent = lazy(() => import('../../containers/Channel/ChannelRecent'));
const ChannelMembers = lazy(() => import('../../containers/Channel/ChannelMembers'));
const ContestLandingPage = lazy(() => import('../../containers/ContestLandingPage/ContestLandingPage'));
const FallContestLandingPage = lazy(() => import('../../containers/FallContestLandingPage/FallContestLandingPage'));
const HalloweenLandingPage = lazy(() => import('../HalloweenLandingPage/HalloweenLandingPage'));
const ContestInternal = lazy(() => import('../../containers/Contest/ContestInternal'));
const ContestPreInternal = lazy(() => import('../../containers/Contest/ContestPreInternal'));
const Contests = lazy(() => import('../../containers/Contest/Contests'));
const ContestsWebViewToken = lazy(() => import('../../containers/Contest/ContestsWebViewToken'));
const ContestsWebView = lazy(() => import('../../containers/Contest/ContestsWebView'));
const ContestInternalWebView = lazy(() => import('../../containers/Contest/ContestsInternalWebView'));
const ContestPreInternalWebView = lazy(() => import('../../containers/Contest/ContestsPreInternalWebView'));
const ContestAdRedirect = lazy(() => import('../../containers/Contest/ContestAdRedirect'));
const DeleteAccount = lazy(() => import('../../containers/DeleteAccount/DeleteAccount'));
const DeleteAccountVerification = lazy(() => import('../../containers/DeleteAccount/DeleteAccountVerification'));
const DeleteAccountSuccess = lazy(() => import('../../containers/DeleteAccount/DeleteAccountSuccess'));
const Discover = lazy(() => import('../../containers/Discover'));
const DownloadHistory = lazy(() => import('../../containers/DownloadHistory/DownloadHistory'));
const Elections = lazy(() => import('../../containers/Elections/Elections'));
const ElectionsInternal = lazy(() => import('../../containers/Elections/ElectionsInternal'));
const ElectionInternalDrillDown = lazy(() => import('../../containers/Elections/ElectionInternalDrillDown'));
const ElectionsWebView = lazy(() => import('../../containers/Elections/ElectionsWebView'));
const ElectionsWebViewToken = lazy(() => import('../../containers/Elections/ElectionsWebViewToken'));
const ElectionsInternalWebView = lazy(() => import('../../containers/Elections/ElectionsInternalWebView'));
const ElectionInternalDrillDownWebView = lazy(() => import('../../containers/Elections/ElectionInternalDrillDownWebView'));
const ElectionsLegendWebView = lazy(() => import('../../containers/Elections/ElectionsLegendWebView'));
const ElectionsLegend = lazy(() => import('../../containers/Elections/ElectionsLegend'));
const EmailConfirmation = lazy(() => import('../../containers/EmailConfirmation'));
const Favorites = lazy(() => import('../../containers/Favorites'));
const ForgotPassword = lazy(() => import('../../containers/ForgotPassword'));
const ForgotPasswordConfirm = lazy(() => import('../../containers/ForgotPasswordConfirm'));
const Interest = lazy(() => import('../../containers/Interest'));
const NotFound = lazy(() => import('../../containers/NotFound'));
const MyLibrary = lazy(() => import('../../containers/MyLibrary'));
const MyNews = lazy(() => import('../../containers/MyNews'));
const MyStats = lazy(() => import('../../containers/MyStats/MyStats'));
const MyStatsPanelWebView = lazy(() => import('../../containers/MyStats/MyStatsPanelWebView'));
const MyStatsWebView = lazy(() => import('../../containers/MyStats/MyStatsWebView'));
const MyStatsWebViewToken = lazy(() => import('../../containers/MyStats/MyStatsWebViewToken'));
const MyStatsWelcome = lazy(() => import('../../containers/MyStats/MyStatsWelcome'));
const Notifications = lazy(() => import('../../containers/Notifications'));
const OnboardInterests = lazy(() => import('../../containers/Onboard/OnboardInterests'));
const OnboardProfileName = lazy(() => import('../../containers/Onboard/OnboardProfileName'));
const OnboardPromocode = lazy(() => import('../../containers/Onboard/OnboardPromocode'));
const OnboardUsername = lazy(() => import('../../containers/Onboard/OnboardUsername'));
const PublisherLandingPage = lazy(() => import('../../containers/PublisherLandingPage/PublisherLandingPage'));
const Premium = lazy(() => import('../../containers/Premium'));
const PremiumPurchase = lazy(() => import('../../containers/Premium/PremiumPurchase'));
const PremiumConfirmation = lazy(() => import('../../containers/Premium/PremiumConfirmation'));
const ReadArticles = lazy(() => import('../../containers/ReadArticles'));
const Referrals = lazy(() => import('../../containers/Referrals/Referrals'));
const ReferralsWebView = lazy(() => import('../../containers/ReferralsWebView/ReferralsWebView'));
const ReferralsWebViewToken = lazy(() => import('../../containers/ReferralsWebView/ReferralsWebViewToken'));
const SignIn = lazy(() => import('../../containers/SignIn'));
const SignOut = lazy(() => import('../../containers/SignOut'));
const SignUp = lazy(() => import('../../containers/SignUp'));
const SessionStatusBridge = lazy(() => import('../../containers/SessionStatusBridge'));
const TopArticles = lazy(() => import('../../containers/TopArticles'));
const WorldCupLp = lazy(() => import('../../containers/WorldCupLp/WorldCupLp'));
const WorldCup2026Iframe = lazy(() => import('../../containers/WorldCup2026Iframe/WorldCup2026Iframe'));
const Writer = lazy(() => import('../../containers/Writer/Writer'));

export default function MainRouter() {
  const [previousUrl, setPreviousUrl] = useState();
  const location = useLocation();
  useEffect(() => { // eslint-disable-line arrow-body-style
    const historyPaths = ['/discover', '/articles', '/topics', '/writers', '/all-publishers', '/publishers'];
    const hasPaths = historyPaths.some((path) => location?.pathname.includes(path) === true);
    if (!hasPaths) {
      localStorage.removeItem('exploreHistory');
    }

    return () => setPreviousUrl(location);
  }, [location]);
  return (
    <>
      <RouteSeo />
      <Suspense fallback={null}>
        <Switch>
          <SignedOutLayout path={routes.auth.signin} exact component={SignIn} maxWidth={600} />
          <SignedOutLayout path={routes.auth.signup} exact component={SignUp} maxWidth={600} />
          <SignedOutLayout path={routes.auth.forgotPassword} exact component={ForgotPassword} allowSigned />
          <SignedOutLayout path={routes.auth.forgotPasswordConfirm} exact component={ForgotPasswordConfirm} allowSigned />
          <SignedOutLayout path={routes.auth.forgotPasswordConfirmLegacy} exact component={ForgotPasswordConfirm} allowSigned />
          <SignedOutLayout path={routes.auth.deleteAccount} exact component={DeleteAccount} allowSigned />
          <SignedOutLayout path={routes.auth.deleteAccountVerification} exact component={DeleteAccountVerification} allowSigned />
          <SignedOutLayout path={routes.auth.deleteAccountSuccess} exact component={DeleteAccountSuccess} allowSigned />
          <SignedOutLayout
            path={routes.emailConfirmation}
            exact
            component={EmailConfirmation}
            allowSigned
            noContentTopMargin
          />
          <Route path={routes.auth.signout} exact component={SignOut} />
          <Route path={routes.sessionStatus} exact component={SessionStatusBridge} />
          <NavigationLayout
            path={routes.publishers.main(':slug')}
            allowUnsigned
            useContainer={false}
            exact
            component={() => <Channel previousUrl={previousUrl} />}
          />
          <NavigationLayout
            path={routes.publishers.topic(':slug', ':topicId')}
            allowUnsigned
            useContainer={false}
            exact
            component={Channel}
          />
          <NavigationLayout path={routes.publishers.about(':slug')} useContainer={false} exact component={ChannelAbout} />
          <NavigationLayout path={routes.publishers.categories(':slug')} useContainer={false} exact component={ChannelCategories} />
          <NavigationLayout path={routes.publishers.recent(':slug')} useContainer={false} exact component={ChannelRecent} />
          <NavigationLayout path={routes.publishers.members(':slug')} useContainer={false} exact component={ChannelMembers} />
          <Route path={routes.writers.main(':slug')} useContainer={false} exact component={() => <Writer previousUrl={previousUrl} />} />
          <NavigationLayout path={routes.news.main} exact component={MyNews} />
          <NavigationLayout path={routes.news.feed(':topicId')} useContainer={false} exact component={MyNews} />
          <Route path={routes.interest.feed(':interestId')} useContainer={false} exact component={Interest} />
          <NavigationLayout
            path={routes.news.article(':id')}
            allowUnsigned
            useContainer={false}
            exact
            component={() => <Article previousUrl={previousUrl} />}
          />
          <NavigationLayout path={routes.news.topArticles} exact component={TopArticles} />
          <Route path={routes.news.sharedArticle(':id')} exact component={SharedArticle} />
          <NavigationLayout path={routes.discover} exact component={Discover} />
          <NavigationLayout path={routes.allPublishers} exact component={AllPublishers} />
          <NavigationLayout path={routes.myLibrary} exact component={MyLibrary} />
          <NavigationLayout path={routes.myStats} exact component={MyStatsWelcome} />
          <NavigationLayout path={routes.myStatsPanel} exact component={MyStats} />
          <Route path={routes.myStatsWebViewToken} exact component={MyStatsWebViewToken} />
          <Route path={routes.myStatsWebView} exact component={MyStatsWebView} />
          <Route path={routes.myStatsPanelWebView} exact component={MyStatsPanelWebView} />
          <NavigationLayout path={routes.downloadHistory} exact component={DownloadHistory} />
          <NavigationLayout path={routes.bookmarks} exact component={Bookmarks} />
          <NavigationLayout path={routes.favorites} exact component={Favorites} />
          <NavigationLayout path={routes.read} exact component={ReadArticles} />
          <NavigationLayout path={routes.notifications} exact component={Notifications} />
          <NavigationLayout path={routes.premium} exact useContainer={false} noPadding component={Premium} />
          <NavigationLayout path={routes.premiumPurchase(':priceId')} exact useContainer={false} noPadding component={PremiumPurchase} />
          <NavigationLayout path={routes.premiumConfirmation} exact component={PremiumConfirmation} />
          <Redirect exact from="/" to={routes.news.main} />
          <Route path={routes.onboard.interests} exact component={OnboardInterests} />
          <Route path={routes.onboard.promoCode} exact component={OnboardPromocode} />
          <Route path={routes.onboard.username} exact component={OnboardUsername} />
          <Route path={routes.onboard.profileName} exact component={OnboardProfileName} />
          <Route path={routes.app.getApp} exact component={AppRedirect} />
          <NavigationLayout path={routes.contestsDetail(':contestId')} exact component={ContestInternal} />
          <NavigationLayout path={routes.contestsPreDetail(':contestId')} exact component={ContestPreInternal} />
          <NavigationLayout path={routes.contests} exact noPadding component={Contests} />
          <Route path={routes.contestsWebViewToken} exact component={ContestsWebViewToken} />
          <Route path={routes.contestsWebView} exact component={ContestsWebView} />
          <Route path={routes.contestsWebViewAdRedirect} exact component={ContestAdRedirect} />
          <Route path={routes.contestsDetailWebView(':contestId')} exact component={ContestInternalWebView} />
          <Route path={routes.contestsPreDetailWebView(':contestId')} exact component={ContestPreInternalWebView} />
          <SmartRedirectLayout path={routes.elections} exact component={Elections} />
          <SmartRedirectLayout path={routes.electionsLegend} exact component={ElectionsLegend} />
          <WebViewLayout path={routes.electionsLegendWebView} exact component={ElectionsLegendWebView} />
          <SmartRedirectLayout path={routes.electionsInternal(':type')} exact component={ElectionsInternal} />
          <SmartRedirectLayout path={routes.electionsInternalDrillDown(':type', ':party')} exact component={ElectionInternalDrillDown} />
          <Route path={routes.electionsWebViewToken} exact component={ElectionsWebViewToken} />
          <WebViewLayout path={routes.electionsWebView} exact component={ElectionsWebView} />
          <WebViewLayout path={routes.electionsInternalWebView(':type')} exact component={ElectionsInternalWebView} />
          <WebViewLayout path={routes.electionsInternalDrillDownWebView(':type', ':party')} exact component={ElectionInternalDrillDownWebView} />
          <Route path={routes.contestLandingPage} exact component={ContestLandingPage} />
          <Route path={routes.fallContestLandingPage} exact component={FallContestLandingPage} />
          <Route path={routes.halloweenLandingPage} exact component={HalloweenLandingPage} />
          <Route path={routes.publisherLandingPage(':slug')} exact component={PublisherLandingPage} />
          <Route path={routes.publisherLandingPageBase} exact component={PublisherLandingPage} />
          <Route path={routes.bulkSubscriptionLpClean(':landingCode')} exact component={BulkSubscriptionLp} />
          <Route path={routes.bulkSubscriptionLp} exact component={BulkSubscriptionLp} />
          <NavigationLayout path={routes.referrals} exact noPadding component={Referrals} />
          <Route path={routes.referralsWebViewToken} exact component={ReferralsWebViewToken} />
          <WebViewLayout path={routes.referralsWebView} exact component={ReferralsWebView} />
          <NavigationLayout path={routes.ambassadorLp} exact component={AmbassadorLp} />
          <Route path={routes.ambassadorLpWebViewToken} exact component={AmbassadorLPWebViewToken} />
          <Route path={routes.ambassadorLpWebView} exact component={AmbassadorLPWebView} />
          <Route path={routes.bracketChallenge(':contestId')} exact component={BracketChallenge} />
          <Route path={routes.bracketLp} exact component={BracketChallengeLp} />
          <Route path={routes.worldCup2026} exact component={WorldCup2026Iframe} />
          <Route path={routes.worldCupLp} exact component={WorldCupLp} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}
